import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchStaffDailyWork, fetchStaffPerformance } from '../../api/analytics';
import { fetchBranches } from '../../api/salon';
import SalaryApprovalsPanel from '../../components/staff/SalaryApprovalsPanel';
import StaffSalaryPanel from '../../components/staff/StaffSalaryPanel';
import DashboardShell from '../../components/ui/DashboardShell';
import EmptyState from '../../components/ui/EmptyState';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';

const TEAL = '#14b8a6';
const CORAL = '#fb7185';
const AMBER = '#fbbf24';
const PURPLE = '#a855f7';
const PIE_COLORS = [TEAL, AMBER, CORAL];

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const TrendBadge = ({ value }) => {
  const { t } = useTranslation();
  if (value === 0 || value == null) return <span className="text-zinc-500">—</span>;
  const positive = value >= 0;
  return (
    <span className={positive ? 'text-emerald-400' : 'text-red-400'}>
      {t('salon.analytics.trendVsLast', { direction: positive ? '↑' : '↓', value: Math.abs(value) })}
    </span>
  );
};

const StaffPerformancePage = () => {
  const { t } = useTranslation();
  const { user, role } = useAuth();
  const { theme } = useTheme();
  const isSalaryOwner = role === 'SALON_MANAGER' || role === 'SUPER_ADMIN';
  const canManageSalaries = isSalaryOwner || !!user?.canManageSalaries;
  const chartTheme = getChartTheme(theme);
  const now = new Date();
  const [branchId, setBranchId] = useState('all');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [sort, setSort] = useState('completed');
  const [selectedStaffId, setSelectedStaffId] = useState('');

  const dateRange = useMemo(() => {
    const lastDay = new Date(year, month, 0).getDate();
    const m = String(month).padStart(2, '0');
    return {
      startDate: `${year}-${m}-01`,
      endDate: `${year}-${m}-${String(lastDay).padStart(2, '0')}`,
    };
  }, [year, month]);

  const branchParam = branchId === 'all' ? 'all' : branchId;
  const stale = 5 * 60 * 1000;

  const { data: branchData } = useQuery({
    queryKey: ['branches'],
    queryFn: fetchBranches,
  });
  const branches = branchData?.branches || [];

  const { data, isLoading } = useQuery({
    queryKey: ['staff-performance', branchParam, dateRange, sort, selectedStaffId],
    queryFn: () =>
      fetchStaffPerformance({
        branchId: branchParam,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sort,
        staffId: selectedStaffId || undefined,
      }),
    staleTime: stale,
  });

  const { data: dailyWorkData } = useQuery({
    queryKey: ['staff-daily-work', branchParam, dateRange, selectedStaffId],
    queryFn: () =>
      fetchStaffDailyWork({
        branchId: branchParam,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        staffId: selectedStaffId,
      }),
    enabled: !!selectedStaffId,
    staleTime: stale,
  });

  const staff = useMemo(() => data?.staff || [], [data]);

  useEffect(() => {
    if (staff.length && !selectedStaffId) {
      setSelectedStaffId(staff[0].staffId);
    }
  }, [staff, selectedStaffId]);
  const summary = data?.summary || {};
  const ratingDistribution = data?.ratingDistribution || { good: 0, neutral: 0, bad: 0, total: 0 };
  const trend = data?.trend || [];

  const selectedStaff = staff.find((s) => s.staffId === selectedStaffId) || staff[0];
  const dailyWork = dailyWorkData?.daily || [];
  const dailyTotals = dailyWorkData?.totals || {};
  const dailyWorkChart = dailyWork.map((d) => ({
    date: d.date.slice(5),
    fullDate: d.date,
    completed: d.completed,
    cancelled: d.cancelled,
    rescheduled: d.rescheduled,
    hoursWorked: d.hoursWorked,
  }));

  const completedChart = staff.slice(0, 8).map((s) => ({
    name: s.staffName.split(' ')[0],
    fullName: s.staffName,
    completed: s.completedAppointments,
    staffId: s.staffId,
  }));

  const ratingChart = staff
    .filter((s) => s.reviewCount > 0)
    .slice(0, 8)
    .map((s) => ({
      name: s.staffName.split(' ')[0],
      fullName: s.staffName,
      rating: s.averageRating,
      staffId: s.staffId,
    }));

  const reviewStackChart = staff
    .filter((s) => s.reviewCount > 0)
    .slice(0, 6)
    .map((s) => ({
      name: s.staffName.split(' ')[0],
      fullName: s.staffName,
      good: s.goodReviews,
      neutral: s.neutralReviews,
      bad: s.badReviews,
      staffId: s.staffId,
    }));

  const pieData = [
    { name: t('staffPerformance.reviews.good'), value: ratingDistribution.good, key: 'good' },
    { name: t('staffPerformance.reviews.neutral'), value: ratingDistribution.neutral, key: 'neutral' },
    { name: t('staffPerformance.reviews.bad'), value: ratingDistribution.bad, key: 'bad' },
  ].filter((d) => d.value > 0);

  const summaryCards = [
    {
      label: t('staffPerformance.summary.completed'),
      value: summary.totalCompleted ?? 0,
      icon: '✓',
      color: 'text-emerald-400',
    },
    {
      label: t('staffPerformance.summary.avgRating'),
      value: summary.averageRating ? `${summary.averageRating} ★` : '—',
      icon: '★',
      color: 'text-amber-400',
    },
    {
      label: t('staffPerformance.summary.reviews'),
      value: summary.totalReviews ?? 0,
      icon: '💬',
      color: 'text-sky-400',
    },
    {
      label: t('staffPerformance.summary.topPerformer'),
      value: summary.topPerformer || '—',
      icon: '🏆',
      color: 'text-glow-400',
    },
  ];

  const handleChartClick = (state) => {
    const id = state?.activePayload?.[0]?.payload?.staffId;
    if (id) setSelectedStaffId(id);
  };

  return (
    <DashboardShell
      title={t('staffPerformance.title')}
      subtitle={t('staffPerformance.subtitle')}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link
          to="/salon/staff"
          className="text-sm text-zinc-500 hover:text-glow-400"
        >
          ← {t('staffPerformance.backToStaff')}
        </Link>
      </div>

      <div className="sticky top-0 z-20 -mx-2 mb-6 space-y-3 rounded-xl border border-zinc-200 bg-zinc-50/95 p-4 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setBranchId('all')}
            className={`rounded-lg px-3 py-1.5 text-xs ${branchId === 'all' ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
          >
            {t('salon.analytics.allBranches')}
          </button>
          {branches.map((b) => (
            <button
              key={b._id}
              type="button"
              onClick={() => setBranchId(b._id)}
              className={`rounded-lg px-3 py-1.5 text-xs ${branchId === b._id ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
            >
              {b.name}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {new Date(2000, m - 1, 1).toLocaleString(undefined, { month: 'long' })}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          >
            {[now.getFullYear(), now.getFullYear() - 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-950"
          >
            <option value="completed">{t('staffPerformance.sort.completed')}</option>
            <option value="revenue">{t('staffPerformance.sort.revenue')}</option>
            <option value="rating">{t('staffPerformance.sort.rating')}</option>
            <option value="reviews">{t('staffPerformance.sort.reviews')}</option>
          </select>
        </div>
      </div>

      {isLoading && (
        <p className="text-zinc-500">{t('common.loading')}</p>
      )}

      {!isLoading && !staff.length && (
        <EmptyState
          icon="📊"
          title={t('staffPerformance.emptyTitle')}
          subtitle={t('staffPerformance.emptySubtitle')}
        />
      )}

      {!isLoading && staff.length > 0 && (
        <div className="space-y-6">
          {isSalaryOwner && <SalaryApprovalsPanel />}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {summaryCards.map((card) => (
              <div
                key={card.label}
                className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900"
              >
                <div className="flex items-start justify-between">
                  <p className="text-xs text-zinc-500">{card.label}</p>
                  <span className="text-lg">{card.icon}</span>
                </div>
                <p className={`mt-2 truncate text-xl font-semibold ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
              <h3 className="text-sm font-medium">{t('staffPerformance.charts.completed')}</h3>
              <p className="mt-1 text-xs text-zinc-500">{t('staffPerformance.charts.completedHint')}</p>
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={completedChart} layout="vertical" margin={{ left: 8, right: 16 }} onClick={handleChartClick}>
                    <CartesianGrid stroke={chartTheme.grid} horizontal={false} />
                    <XAxis type="number" tick={{ fill: chartTheme.tick, fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: chartTheme.tick, fontSize: 11 }} width={56} />
                    <Tooltip
                      contentStyle={{
                        background: chartTheme.tooltipBg,
                        border: `1px solid ${chartTheme.tooltipBorder}`,
                        borderRadius: 8,
                        color: chartTheme.tooltipColor,
                      }}
                      formatter={(v, _n, props) => [v, props.payload.fullName]}
                    />
                    <Bar dataKey="completed" fill={TEAL} radius={[0, 4, 4, 0]} cursor="pointer" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
              <h3 className="text-sm font-medium">{t('staffPerformance.charts.ratings')}</h3>
              <p className="mt-1 text-xs text-zinc-500">{t('staffPerformance.charts.ratingsHint')}</p>
              <div className="mt-4 h-72">
                {ratingChart.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ratingChart} onClick={handleChartClick}>
                      <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                      <YAxis domain={[0, 5]} tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          background: chartTheme.tooltipBg,
                          border: `1px solid ${chartTheme.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                        formatter={(v, _n, props) => [`${v} ★`, props.payload.fullName]}
                      />
                      <Bar dataKey="rating" fill={AMBER} radius={[4, 4, 0, 0]} cursor="pointer" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="flex h-full items-center justify-center text-sm text-zinc-500">
                    {t('staffPerformance.noReviewsYet')}
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
              <h3 className="text-sm font-medium">{t('staffPerformance.charts.reviewMix')}</h3>
              <p className="mt-1 text-xs text-zinc-500">{t('staffPerformance.charts.reviewMixHint')}</p>
              <div className="mt-4 h-72">
                {reviewStackChart.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reviewStackChart}>
                      <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          background: chartTheme.tooltipBg,
                          border: `1px solid ${chartTheme.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                      <Bar dataKey="good" stackId="r" fill={TEAL} name={t('staffPerformance.reviews.good')} />
                      <Bar dataKey="neutral" stackId="r" fill={AMBER} name={t('staffPerformance.reviews.neutral')} />
                      <Bar dataKey="bad" stackId="r" fill={CORAL} name={t('staffPerformance.reviews.bad')} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="flex h-full items-center justify-center text-sm text-zinc-500">
                    {t('staffPerformance.noReviewsYet')}
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
              <h3 className="text-sm font-medium">{t('staffPerformance.charts.distribution')}</h3>
              <p className="mt-1 text-xs text-zinc-500">{t('staffPerformance.charts.distributionHint')}</p>
              <div className="mt-4 h-72">
                {pieData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={entry.key} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: chartTheme.tooltipBg,
                          border: `1px solid ${chartTheme.tooltipBorder}`,
                          borderRadius: 8,
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="flex h-full items-center justify-center text-sm text-zinc-500">
                    {t('staffPerformance.noReviewsYet')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {selectedStaff && (
            <div className="rounded-xl border border-glow-600/30 bg-glow-600/5 p-5">
              <div className="flex flex-wrap items-center gap-4">
                {selectedStaff.profilePicture ? (
                  <img
                    src={selectedStaff.profilePicture}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-200 text-lg dark:bg-zinc-800">
                    {selectedStaff.staffName?.[0]}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{selectedStaff.staffName}</h3>
                  <p className="text-sm text-zinc-500">{t('staffPerformance.detailSubtitle')}</p>
                </div>
                <select
                  value={selectedStaffId || selectedStaff.staffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  className="ml-auto rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                >
                  {staff.map((s) => (
                    <option key={s.staffId} value={s.staffId}>{s.staffName}</option>
                  ))}
                </select>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.metrics.completed')}</p>
                  <p className="text-xl font-semibold">{selectedStaff.completedAppointments}</p>
                  <TrendBadge value={selectedStaff.completedChange} />
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.work.hours')}</p>
                  <p className="text-xl font-semibold">{selectedStaff.hoursWorked ?? dailyTotals.hoursWorked ?? 0}h</p>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.work.cancelled')}</p>
                  <p className="text-xl font-semibold text-red-400">{selectedStaff.cancelledAppointments ?? 0}</p>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.work.rescheduled')}</p>
                  <p className="text-xl font-semibold text-amber-400">{selectedStaff.rescheduledAppointments ?? 0}</p>
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.metrics.revenue')}</p>
                  <p className="text-xl font-semibold">{formatLkr(selectedStaff.revenue)}</p>
                  <TrendBadge value={selectedStaff.revenueChange} />
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.metrics.rating')}</p>
                  <p className="text-xl font-semibold">
                    {selectedStaff.reviewCount ? `${selectedStaff.averageRating} ★` : '—'}
                  </p>
                  <TrendBadge value={selectedStaff.ratingChange} />
                </div>
                <div className="rounded-lg border border-zinc-200 bg-white/80 p-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <p className="text-xs text-zinc-500">{t('staffPerformance.metrics.satisfaction')}</p>
                  <p className="text-xl font-semibold">
                    {selectedStaff.reviewCount ? `${selectedStaff.satisfactionScore}%` : '—'}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {t('staffPerformance.metrics.positiveRate', { rate: selectedStaff.positiveRate })}
                  </p>
                </div>
                {canManageSalaries && selectedStaff.salary && (
                  <div className="rounded-lg border border-glow-600/30 bg-glow-600/10 p-3 sm:col-span-2 lg:col-span-4">
                    <p className="text-xs text-zinc-500">{t('staffPerformance.salary.configured')}</p>
                    <p className="mt-1 text-sm">
                      {t('staffPerformance.salary.summary', {
                        daily: formatLkr(selectedStaff.salary.dailyAmount),
                        weekly: formatLkr(selectedStaff.salary.weeklyAmount),
                        monthly: formatLkr(selectedStaff.salary.monthlyAmount),
                      })}
                    </p>
                    {selectedStaff.estimatedPay && (
                      <p className="mt-1 text-xs text-zinc-500">
                        {t('staffPerformance.salary.estimated', {
                          daily: formatLkr(selectedStaff.estimatedPay.daily),
                        })}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {dailyWorkChart.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium">{t('staffPerformance.work.dailyChart')}</h4>
                  <p className="mt-1 text-xs text-zinc-500">{t('staffPerformance.work.dailyChartHint')}</p>
                  <div className="mt-3 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dailyWorkChart}>
                        <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: chartTheme.tick, fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            background: chartTheme.tooltipBg,
                            border: `1px solid ${chartTheme.tooltipBorder}`,
                            borderRadius: 8,
                          }}
                          labelFormatter={(_l, payload) => payload?.[0]?.payload?.fullDate}
                        />
                        <Legend />
                        <Bar dataKey="completed" fill={TEAL} name={t('staffPerformance.metrics.completed')} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="cancelled" fill={CORAL} name={t('staffPerformance.work.cancelled')} />
                        <Bar dataKey="rescheduled" fill={AMBER} name={t('staffPerformance.work.rescheduled')} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {canManageSalaries && (
                <div className="mt-6">
                  <StaffSalaryPanel staffId={selectedStaff.staffId} isOwner={isSalaryOwner} />
                </div>
              )}

              {trend.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium">{t('staffPerformance.charts.trend')}</h4>
                  <div className="mt-3 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={trend}>
                        <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                        <XAxis dataKey="label" tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                        <YAxis yAxisId="left" allowDecimals={false} tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tick={{ fill: chartTheme.tick, fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            background: chartTheme.tooltipBg,
                            border: `1px solid ${chartTheme.tooltipBorder}`,
                            borderRadius: 8,
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="completed" fill={TEAL} name={t('staffPerformance.metrics.completed')} radius={[4, 4, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="averageRating" stroke={PURPLE} strokeWidth={2} dot name={t('staffPerformance.metrics.rating')} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/80">
                <tr>
                  <th className="px-4 py-3">#</th>
                  <th className="px-4 py-3">{t('staffPerformance.table.staff')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.table.completed')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.work.hours')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.work.cancelled')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.table.revenue')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.table.rating')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.reviews.good')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.reviews.bad')}</th>
                  <th className="px-4 py-3">{t('staffPerformance.table.satisfaction')}</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s, i) => (
                  <tr
                    key={s.staffId}
                    onClick={() => setSelectedStaffId(s.staffId)}
                    className={`cursor-pointer border-b border-zinc-200/50 transition hover:bg-zinc-100/50 dark:border-zinc-800/50 dark:hover:bg-zinc-800/40 ${
                      (selectedStaffId || staff[0]?.staffId) === s.staffId ? 'bg-glow-600/10' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-zinc-500">{i + 1}</td>
                    <td className="px-4 py-3 font-medium">{s.staffName}</td>
                    <td className="px-4 py-3">{s.completedAppointments}</td>
                    <td className="px-4 py-3">{s.hoursWorked ?? 0}h</td>
                    <td className="px-4 py-3 text-red-400">{s.cancelledAppointments ?? 0}</td>
                    <td className="px-4 py-3">{formatLkr(s.revenue)}</td>
                    <td className="px-4 py-3">{s.reviewCount ? `${s.averageRating} ★` : '—'}</td>
                    <td className="px-4 py-3 text-emerald-400">{s.goodReviews}</td>
                    <td className="px-4 py-3 text-red-400">{s.badReviews}</td>
                    <td className="px-4 py-3">{s.reviewCount ? `${s.satisfactionScore}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardShell>
  );
};

export default StaffPerformancePage;
